import axios from 'axios';

import {
  API_KEY,
  URL,
  subscribeToConfigChanges
} from '~/common/helpers/environment';
import { Domain, DomainAccessType, DomainUsersData } from '~/entities/Domain';

export interface ApiResponse {
  success: boolean;
  errorMessage?: string;
}

export interface FetchDomainsResponse extends ApiResponse {
  data?: Array<Domain>;
}

type UsersData = {
  content: Array<{
    totalUsers: number;
    users: DomainUsersData[];
  }>;
};
export interface FetchUsersDataResponse extends ApiResponse {
  data?: {
    totalUsers: number;
    users: DomainUsersData[];
  };
}

type AxiosHeader = [string, string];

const createAxiosDomain = (config?: any) =>
  axios.create({
    headers: {
      'Content-type': 'application/json',
      apikey: config?.API_KEY || API_KEY
    }
  });

let axiosDomain = createAxiosDomain();

subscribeToConfigChanges((config: any) => {
  axiosDomain = createAxiosDomain(config);
});

const domains = {
  setHeaders(headers: AxiosHeader[]): void {
    headers.forEach(([key, value]) => {
      axiosDomain.defaults.headers.common[key] = value;
    });
  },
  async updateDomain(
    realm: string,
    domainData: {
      domainName: string;
      domainAccessType: DomainAccessType;
      users: Array<{
        userEmail: string;
        isDIGIBEEAccessAllowed: boolean;
      }>;
    }
  ): Promise<ApiResponse> {
    try {
      await axiosDomain.put<null>(
        `${URL}/design/realms/${encodeURIComponent(realm)}/domains`,
        domainData
      );
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        errorMessage: 'unknown_error'
      };
    }
  },
  async createDomain(
    realm: string,
    domainAccessType: DomainAccessType,
    domainName: string
  ): Promise<ApiResponse> {
    try {
      await axiosDomain.post<null>(
        `${URL}/design/realms/${encodeURIComponent(realm)}/domains`,
        {
          domainAccessType,
          domainName
        }
      );
      return {
        success: true
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409)
          return {
            success: false,
            errorMessage: 'domain_address_already_exists'
          };
        return {
          success: false,
          errorMessage: 'api_error'
        };
      }
      return {
        success: false,
        errorMessage: 'unknown_error'
      };
    }
  },
  async fetchDomains(realm: string): Promise<FetchDomainsResponse> {
    try {
      const result = await axiosDomain.get<Array<Domain>>(
        `${URL}/design/realms/${encodeURIComponent(realm)}/domains`
      );
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        errorMessage: 'unknown_error'
      };
    }
  },
  async fetchUsersData(
    realm: string,
    domain: string,
    page: number,
    size: number,
    filter?: string
  ): Promise<FetchUsersDataResponse> {
    try {
      const endpoint = `${URL}/design/realms/${encodeURIComponent(realm)}/domains/${encodeURIComponent(
        domain
      )}/users`;

      const result = await axiosDomain.get<UsersData>(endpoint, {
        params: {
          size,
          page,
          ...(filter ? { filter } : {})
        }
      });
      return {
        success: true,
        data: result.data.content[0]
      };
    } catch (error) {
      return {
        success: false,
        errorMessage: 'unknown_error'
      };
    }
  },
  async activateAccessRules(realm: string): Promise<ApiResponse> {
    try {
      await axiosDomain.patch(
        `${URL}/design/realms/${encodeURIComponent(realm)}/domains/activate-rules`
      );
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        errorMessage: 'unknown_error'
      };
    }
  }
};

export default domains;
